import React from "react";
import tw from "twin.macro";
import styled from "styled-components";
import { SectionHeading, Subheading as SubheadingBase } from "../../components/misc/Headings.js";
import { PrimaryButton as PrimaryButtonBase } from "../../components/misc/Buttons.js";
import TeamIllustrationSrc from "../../images/team-illustration-2.svg";
import { ReactComponent as SvgDotPattern } from "../../images/dot-pattern.svg";
import { ReactComponent as BriefcaseIcon } from "feather-icons/dist/icons/briefcase.svg";
import { ReactComponent as MoneyIcon } from "feather-icons/dist/icons/dollar-sign.svg";
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles({
  image:{'max-width': '100%',
   'height': 'auto'},
   title:{
    'font-family': 'Inter, system-ui',
    'color': 'rgb(36, 62, 99)',
    'margin-left': '0.75rem',
    'font-weight': '700',
    'font-size': '1.25rem',
   },
   subTitle:{
    'font-family': 'Inter, system-ui',  
    'font-weight':'400',
    'margin-top': '1rem',
    'text-align': 'center',
    'color': 'rgb(113, 128, 150)',
    'line-height': '1.625',
    'text-align': 'left'
   },
   dollarIcon:{
    'margin-left': 'auto!important',
    'margin-right': 'auto!important',
    'display': 'inline-block!important',
    'border-color': 'rgb(100, 21, 255)!important',
    'text-align': 'center!important',
    'padding': '0.5rem!important',
    'flex-shrink': '0!important',
    'border-radius': '9999px!important',
    'border-width': '0px!important',
    'background-color': 'rgb(154, 230, 180)!important',
    'color': 'rgb(39, 103, 73)!important',
  },
  bagIcon:{
    'margin-left': 'auto!important',
    'margin-right': 'auto!important',
    'display': 'inline-block!important',
    'border-color': 'rgb(100, 21, 255)!important',
    'text-align': 'center!important',
    'padding': '0.5rem!important',
    'flex-shrink': '0!important',
    'border-radius': '9999px!important',
    'border-width': '0px!important',
    'background-color': 'rgb(254, 178, 178)!important',
    'color': 'rgb(155, 44, 44)!important',
  }
 }
);
const Container = tw.div`relative`;
const TwoColumn = tw.div`flex flex-col md:flex-row justify-between max-w-screen-xl mx-auto py-20 md:py-24 items-center`;
const Column = tw.div`w-full max-w-md mx-auto md:max-w-none md:mx-0`;
const ImageColumn = tw(Column)`md:w-6/12 flex-shrink-0 relative`;
const TextColumn = styled(Column)(props => [
  tw`md:w-6/12 mt-16 md:mt-0`,
  props.textOnLeft ? tw`md:mr-12 lg:mr-16 md:order-first` : tw`md:ml-12 lg:ml-16 md:order-last`
]);

const Image = styled.img(props => [
  props.imageRounded && tw`rounded`,
  props.imageBorder && tw`border`,
  props.imageShadow && tw`shadow`
]);

const DecoratorBlob = tw(
  SvgDotPattern
)`w-20 h-20 absolute right-0 bottom-0 transform translate-x-1/2 translate-y-1/2 fill-current text-gray-500 `;

const TextContent = tw.div`lg:py-8 text-center md:text-left`;

const Subheading = tw(SubheadingBase)`text-center md:text-left`;
const Heading = tw(
  SectionHeading
)`mt-4 font-black text-left text-3xl sm:text-4xl lg:text-5xl text-center md:text-left leading-tight`;
const Description = tw.p`mt-8 text-center md:text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-gray-100`;

const Features = tw.div`mx-auto md:mx-0 flex flex-col lg:flex-row max-w-xs lg:max-w-none`;
const Feature = tw.div`mt-10 lg:mt-8 flex items-center md:items-start flex-col md:mr-8 last:mr-0`;

const FeatureHeadingContainer = tw.div`flex items-center`;
const FeatureIconContainer = styled.div`
  ${tw`mx-auto inline-block border border-gray-500 text-gray-500 text-center rounded p-2 flex-shrink-0`}
  ${props => [
    props.iconRoundedFull && tw`rounded-full`,
    props.iconFilled && tw`border-0 bg-gray-500 text-gray-100`
  ]}
  svg {
    ${tw`w-5 h-5`}
  }
`;
const FeatureHeading = tw.div`ml-3 font-bold text-xl`;

const FeatureDescription = tw.div`mt-4 text-center md:text-left text-gray-600 leading-relaxed`;

const PrimaryButton = styled(PrimaryButtonBase)(props => [
  tw`mt-12 text-sm inline-block mx-auto md:mx-0`,
  props.buttonRounded && tw`rounded-full`
]);

export default ({
  subheading = "Our Expertise",
  heading = (
    <>
      Designed & Developed by <span tw="text-gray-500">Professionals.</span>
    </>
  ),
  description = "Your satisfaction with our services is our mission, we will give you good quality services with acceptable prices",
  imageSrc = TeamIllustrationSrc,
  buttonRounded = true,
  imageRounded = true,
  imageBorder = false,
  imageShadow = false,
  showDecoratorBlob = false,
  textOnLeft = true,
  features = null,
  iconRoundedFull = true,
  iconFilled = true,
  iconContainerCss = null
}) => {
  const classes = useStyles();
  // The textOnLeft boolean prop can be used to display either the text on left or right side of the image.

  /*
   * Change the features variable as you like, add or delete objects
   * `icon` must be a React SVG component. See how BriefcaseIcon is imported above. For a full list of available icons, see Feather Icons.
   */
  const defaultFeatures = [
    {
      Icon: BriefcaseIcon,
      title: "Professionalism",
      description: "We assure you that your data will be always safe and our services have high availability.",
      iconContainerCss: tw`bg-teal-300 text-teal-800`
    },
    {
      Icon: MoneyIcon,
      title: "Affordable",
      description: "We promise to offer you the best rate we can - at par with the value you will gain.",
      iconContainerCss: tw`bg-red-300 text-red-800`
    }
  ];

  if (!features) features = defaultFeatures;

  return (
    <Container>
      <TwoColumn>
        <ImageColumn>
          <Image  className={classes.image} src={imageSrc} imageBorder={imageBorder} imageShadow={imageShadow} imageRounded={imageRounded} />
          {showDecoratorBlob && <DecoratorBlob />}
        </ImageColumn>
        <TextColumn textOnLeft={textOnLeft}>
          <TextContent>
            <Subheading>{subheading}</Subheading>
            <Heading className="Title">{heading}</Heading>
            <Description className="Description">{description}</Description>
            <Features>
              {features.map((feature, index) => (
                <Feature key={index}>
                  <FeatureHeadingContainer>
                    <FeatureIconContainer className={ index ==0 ? classes.dollarIcon : classes.bagIcon}
                      iconFilled={iconFilled}
                      iconRoundedFull={iconRoundedFull}
                      css={feature.iconContainerCss || iconContainerCss}
                    >
                      {<feature.Icon />}
                    </FeatureIconContainer>
                    <FeatureHeading className={classes.title}>{feature.title}</FeatureHeading>
                  </FeatureHeadingContainer>
                  <FeatureDescription className={classes.subTitle}>{feature.description}</FeatureDescription>
                </Feature>
              ))}
            </Features>

          </TextContent>
        </TextColumn>
      </TwoColumn>
    </Container>
  );
};
